/** Native attik report payload — mirrors attik-report-service InspectionReportPayload. */

import type {
  ReportExternalLink,
  VideoLink,
} from '@attik/types/videoLink.types';
import type { MultipleChoiceGroup } from '@attik/types/templateComment.types';
import { sanitizeRichHtml } from '@/util/functions/sanitizeRichHtml';
import type { LocationTag } from '@attik/types/locationOptions';

export type AttikObservationType = 'info' | 'defect' | 'limitation';

/** Defect severity sourced from the matched template comment (defect only). */
export type AttikDefectLevel = string;

export type AttikReportMedia = {
  id: string;
  url: string;
  sourceMediaId?: string;
  origin?: 'template_default';
  caption?: string;
  bucketId?: string;
  createdAt?: string;
};

export type AttikOverviewBucket = {
  bucketId: string;
  label: string;
  required?: boolean;
  minCount?: number;
  order?: number;
};

export type AttikReportObservation = {
  commentId: string;
  commentType: AttikObservationType;
  templateCommentId?: string;
  promotedTemplateCommentId?: string;
  defectLevel?: AttikDefectLevel;
  sourceCommentId?: string;
  sourceFindingIndex?: string;
  reinspectionOutcome?: string;
  flaggedForReview?: boolean;
  narrative: string;
  finalNarrative?: string;
  location?: string;
  order?: number;
  /** Inspector-picked catalog multi-select values (LP-140). */
  selectedMultipleChoiceGroups?: MultipleChoiceGroup[];
  /** Catalog option groups for `templateCommentId` (editor payloads). */
  templateMultipleChoiceGroups?: MultipleChoiceGroup[];
  /** The catalog comment's own groups; editors can't remove these options. */
  catalogMultipleChoiceGroups?: MultipleChoiceGroup[];
  mediaIds: string[];
  media?: AttikReportMedia[];
  externalVideoLink?: VideoLink;
  createdAt: string;
  updatedAt?: string;
  updatedBy?: string;
  /** Editor-only: original comment author id. */
  createdBy?: string;
  /** Editor-only: display name for the original author. */
  authorName?: string;
  /** Editor-only: inspector thumb, else the user picture. */
  authorImage?: string;
  seeded?: boolean;
  /** Pipeline status when shown read-only in the web editor. */
  status?: string;
  /** False for in-flight mobile comments; editable when omitted or true. */
  editable?: boolean;
  /** Persisted inspector title, when set. */
  title?: string;
  /** Suggested placement when awaiting mobile confirmation. */
  suggestedSectionId?: string;
  suggestedSubSectionId?: string;
  /** Editor-only: AI catalog matches + narrative for the web pick step. */
  templateCommentIdMatches?: { templateCommentId: string; matchConfidence: number }[];
  suggestedNarrative?: string;
  suggestedTitle?: string;
  ingestContext?: string;
  /** Spectora field shape for legacy-look adapter (optional). */
  legacySkin?: {
    identifier?: string;
    name?: string;
    answer_type?: string;
    value?: string | null;
    value2?: string | null;
    choices?: string | null;
    text?: string | null;
    recommendation?: string | null;
    /** Spectora defect category (-1 / 0 / 1 or 1 / 2 / 3). */
    category?: number | null;
  };
};

export function isObservationEditable(obs: AttikReportObservation): boolean {
  return obs.editable !== false;
}

export function observationPendingLabel(obs: AttikReportObservation): string {
  const status = obs.status?.trim().toLowerCase();
  if (status === 'processing' || status === 'videoprocessed') return 'Processing';
  return 'Pending';
}

/** Resolved media with a loadable URL, falling back to empty when unavailable. */
export function observationMedia(obs: AttikReportObservation): AttikReportMedia[] {
  return (obs.media ?? []).filter((m) => m.url);
}

export type AttikStylesMaterialsFieldDef = {
  fieldId: string;
  label: string;
  valueType: string;
  options?: string[];
  required?: boolean;
  /** Present when field is driven by catalog multichoice / LLM. */
  mapsFrom?: 'multichoice' | 'llm';
};

export type AttikStylesMaterialsFieldValue = {
  fieldId: string;
  value: string;
};

export type AttikReportSubsection = {
  subSectionId: string;
  label: string;
  description: string;
  order: number;
  optional?: boolean;
  activated?: boolean;
  required?: boolean;
  /** Required item hidden via LP-367 overrides — Overview NP row. */
  notPresent?: boolean;
  overviewNarrative?: string;
  sops?: string;
  stylesMaterials: {
    fieldDefs: AttikStylesMaterialsFieldDef[];
    values: AttikStylesMaterialsFieldValue[];
    version: number;
    warnings: string[];
  };
  observations: AttikReportObservation[];
  completion: AttikPlacementCompletion;
};

export type AttikPlacementCompletion = {
  complete: boolean;
  missingRequired: boolean;
  missingSmFieldIds: string[];
  /** Section-level entries only — the section has no overview photo yet. */
  missingOverviewMedia?: boolean;
  missingOverviewBucketIds?: string[];
};

export type AttikReportSection = {
  sectionId: string;
  label: string;
  /** Inspector helper text — editor payload only, never on the client report (ATT-3552). */
  description?: string;
  order: number;
  /** Display number from the template order; omitted sections keep their slot. */
  number?: number;
  icon?: string;
  optional?: boolean;
  requiresOverviewMedia?: boolean;
  hideOverviewMedia?: boolean;
  overviewBuckets?: AttikOverviewBucket[];
  overviewNarrative?: string;
  sops?: string;
  overviewMedia?: AttikReportMedia[];
  /** Resolved display heading for the section-level S&M block (ATT-3278). */
  stylesMaterialsHeading?: string;
  /** Section-level S&M (system overview); subsection S&M may still exist in older payloads. */
  stylesMaterials?: {
    fieldDefs: AttikStylesMaterialsFieldDef[];
    values: AttikStylesMaterialsFieldValue[];
    version: number;
    warnings: string[];
  };
  subsections: AttikReportSubsection[];
};

export type AttikNativeReportPayload = {
  success: boolean;
  inspectionId: string;
  companyId: string;
  reportTemplateId: string;
  reportTemplateLabel?: string;
  templateVersion?: string;
  /** Template toggle for the legacy-skin Overview grid (LP-389). */
  displayOverviewGrid?: boolean;
  /** Resolved template → company toggle for comment number counts (ATT-3019). */
  showNumberCounts?: boolean;
  /** Present (false) only when the template hides the intro's finding-level guide. */
  displayLevelGuide?: false;
  /** Present (false) only when the template hides the report intro block entirely. */
  displayReportIntro?: false;
  /** Template-defined legacy-skin section tabs (LP-393). */
  reportTabs?: import('@attik/types/reportTemplate.types').ReportTab[];
  overviewRatingLabels?: import('@attik/types/derivedInspectionGrid').OverviewRatingLabels;
  /** Summary-table severity column. Unset means "Severity" (ATT-3304). */
  severityHeading?: string;
  /** Template boilerplate (HTML) rendered ahead of the table of contents (ATT-2652). */
  boilerplate?: string;
  /** Defect severity config from the report template. */
  defectLevels?: {
    id: string;
    label: string;
    blurb?: string;
    color: string;
    order: number;
  }[];
  reinspectionOutcomes?: import('@attik/types/reinspectionOutcomes.types').ReinspectionOutcomeConfig[];
  attikReportType?: import('@attik/types/report.types').AttikReportType;
  sourceReportId?: string;
  locationEnabled?: boolean;
  locationOptions?: string[];
  /** Grouped location tags for the chained picker (ATT-3056). */
  locationTags?: LocationTag[];
  /** Editor payload only. Public native payload and PDF omit this. */
  externalLinks?: ReportExternalLink[];
  warnings?: string[];
  sections: AttikReportSection[];
  /**
   * Editor only: AI-ready comments awaiting confirmation
   * (not yet placed into a subsection).
   */
  readyComments?: AttikReportObservation[];
  completion?: {
    complete?: boolean;
    percentComplete: number;
    subsections?: (AttikPlacementCompletion & {
      sectionId: string;
      subSectionId: string;
    })[];
    [key: string]: unknown;
  };
};

export function observationNarrativeRaw(obs: AttikReportObservation): string {
  return (obs.finalNarrative?.trim() || obs.narrative?.trim() || '').trim();
}

export function decodeReportHtmlEntities(raw: string): string {
  return raw
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
}

/** Grouped tags for the location picker; older payloads only carry flat labels. */
export function payloadLocationTags(
  payload: Pick<AttikNativeReportPayload, 'locationTags' | 'locationOptions'>,
): LocationTag[] {
  if (payload.locationTags?.length) return payload.locationTags;
  return (payload.locationOptions ?? []).map((label) => ({ label, group: 'user' }));
}

/** Bubble props for an observation's in-view editor: catalog groups and the saved picks. */
export function observationBubbleProps(obs: AttikReportObservation): {
  templateCommentId?: string;
  optionGroups: MultipleChoiceGroup[];
  catalogGroups: MultipleChoiceGroup[];
  selectedGroups: MultipleChoiceGroup[];
} {
  return {
    templateCommentId: obs.templateCommentId?.trim() || undefined,
    optionGroups: obs.templateMultipleChoiceGroups ?? [],
    catalogGroups: obs.catalogMultipleChoiceGroups ?? [],
    selectedGroups: obs.selectedMultipleChoiceGroups ?? [],
  };
}

export function observationDisplayText(obs: AttikReportObservation): string {
  const raw = observationNarrativeRaw(obs);
  if (!raw) return '';
  const stripped = !/<\/?[a-z][\s\S]*>/i.test(raw)
    ? raw
    : raw
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/\s+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
  return decodeReportHtmlEntities(stripped);
}

export function observationDisplayHtml(obs: AttikReportObservation): string {
  const raw = observationNarrativeRaw(obs);
  if (!raw) return '';
  if (/<\/?[a-z][\s\S]*>/i.test(raw)) {
    return sanitizeRichHtml(raw);
  }
  const escaped = raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .split(/\n\n+/)
    .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
}
